<template>
  <div class="zvp-page">
    <!-- ── TOKEN LOADING ─────────────────────────────────────── -->
    <div v-if="state === 'loading'" class="zvp-center-screen">
      <v-progress-circular indeterminate color="primary" size="56" />
      <p class="body-2 grey--text mt-4">Verifying link…</p>
    </div>

    <!-- ── VALID TOKEN ───────────────────────────────────────── -->
    <template v-else-if="state === 'valid'">
      <!-- Header -->
      <div class="zvp-header">
        <img
          src="/assets/SDLOGO_40.png"
          alt="StorageDefender"
          class="zvp-logo"
        />
      </div>

      <!-- Alert banner -->
      <div class="zvp-alert-banner">
        <v-icon color="amber darken-2" class="mr-2">mdi-bell-alert</v-icon>
        <div>
          <div class="zvp-alert-title">Motion Detected in Your Unit</div>
          <div
            v-if="tokenData.unitName || tokenData.facilityName"
            class="zvp-alert-sub"
          >
            Motion was recorded
            <template v-if="tokenData.unitName">
              in Unit <strong>{{ tokenData.unitName }}</strong></template
            >
            <template v-if="tokenData.facilityName">
              at <strong>{{ tokenData.facilityName }}</strong></template
            >
            on {{ formatTs(tokenData.startTimestamp) }}.
          </div>
        </div>
      </div>

      <!-- Incident details table -->
      <div class="zvp-details-card">
        <div class="zvp-details-title">INCIDENT DETAILS</div>
        <div class="zvp-details-grid">
          <span class="zvp-dl">Unit</span>
          <span class="zvp-dv">{{ tokenData.unitName || "N/A" }}</span>
          <span class="zvp-dl">Smart Zone</span>
          <span class="zvp-dv">{{ tokenData.zoneName }}</span>
          <template v-if="tokenData.cameraName">
            <span class="zvp-dl">Camera</span>
            <span class="zvp-dv">{{ tokenData.cameraName }}</span>
          </template>
          <span class="zvp-dl">Facility</span>
          <span class="zvp-dv">{{ tokenData.facilityName }}</span>
          <span class="zvp-dl">Date &amp; Time</span>
          <span class="zvp-dv">{{ formatTs(tokenData.startTimestamp) }}</span>
          <!-- Status only applies to a unit incident; hide it for a zone incident. -->
          <template v-if="tokenData.unitName">
            <span class="zvp-dl">Status</span>
            <span class="zvp-dv">
              <span class="zvp-status zvp-status--active">
                &#x25cf; Unauthorized
              </span>
            </span>
          </template>
        </div>
      </div>

      <!-- ── EVENTS LOADING ──────────────────────────────────── -->
      <div v-if="eventsLoading" class="zvp-player-skeleton">
        <v-progress-circular indeterminate color="primary" size="40" />
        <p class="caption grey--text mt-3">Loading video clips…</p>
      </div>

      <!-- ── CLIP PLAYER ─────────────────────────────────────── -->
      <template v-else-if="!noEventsDetected">
        <!-- Dark clip header -->
        <div class="zvp-clip-header">
          <span class="zvp-clip-cam">
            {{ tokenData.cameraName || tokenData.zoneName }}
          </span>
          <span class="zvp-clip-badge">CLIP</span>
        </div>

        <!-- Event type radio (only when both motion + person exist) -->
        <div v-if="hasBothEventTypes" class="zvp-radio-row">
          <v-radio-group
            v-model="selectedEventType"
            row
            hide-details
            class="mt-0 pt-0"
          >
            <v-radio label="Motion" value="een.motionDetectionEvent.v1" />
            <v-radio label="Person" value="een.personDetectionEvent.v1" />
          </v-radio-group>
        </div>

        <!-- Video / thumbnail area -->
        <div class="zvp-player-area">
          <video
            v-if="videoData"
            :key="videoKey"
            controls
            autoplay
            playsinline
            class="zvp-video"
          >
            <source
              :src="`data:video/mp4;base64,${videoData}`"
              type="video/mp4"
            />
          </video>

          <div
            v-else-if="currentThumb && currentThumb.thumbnail"
            class="zvp-thumb-wrap"
            @click="loadVideo"
          >
            <img
              :src="'data:image/jpeg;base64,' + currentThumb.thumbnail"
              class="zvp-thumb-img"
              alt="Event thumbnail"
            />
            <div class="zvp-play-overlay">
              <v-btn icon color="white" x-large :disabled="videoLoading">
                <v-progress-circular
                  v-if="videoLoading"
                  indeterminate
                  color="white"
                  size="36"
                />
                <v-icon v-else size="64">fas fa-play-circle</v-icon>
              </v-btn>
            </div>
            <div v-if="currentThumb.startTimestamp" class="zvp-thumb-ts">
              {{ formatTs(currentThumb.startTimestamp) }}
              <span class="zvp-motion-badge">MOTION DETECTED</span>
            </div>
          </div>

          <div v-else class="zvp-player-skeleton">
            <v-progress-circular indeterminate color="primary" size="40" />
            <p class="caption grey--text mt-3">Loading clip…</p>
          </div>
        </div>

        <!-- Clip footer: camera label + download -->
        <div class="zvp-clip-footer">
          <div>
            <div class="zvp-clip-footer-label">Zone Camera</div>
            <div class="zvp-clip-footer-cam">
              {{ tokenData.cameraName || tokenData.zoneName }}
            </div>
          </div>
          <a
            v-if="videoData"
            :href="`data:video/mp4;base64,${videoData}`"
            :download="`clip-${currentIndex + 1}.mp4`"
            class="zvp-download-link"
          >
            <v-icon small class="mr-1">mdi-download</v-icon>Download Clip
          </a>
        </div>

        <!-- Prev / Next navigation -->
        <div class="zvp-nav">
          <v-btn
            class="zvp-nav-btn zvp-prev"
            depressed
            :disabled="currentIndex <= 0 || loadingNext"
            @click="prevClip"
          >
            <v-icon left>fas fa-chevron-left</v-icon>Prev
          </v-btn>
          <span class="zvp-page-count body-2">
            {{ currentIndex + 1 }}&nbsp;/&nbsp;{{ thumbnailsList.length
            }}{{ nextPageToken ? "+" : "" }}
          </span>
          <v-btn
            class="zvp-nav-btn zvp-next"
            depressed
            :disabled="
              (currentIndex >= thumbnailsList.length - 1 && !nextPageToken) ||
              loadingNext
            "
            @click="nextClip"
          >
            <v-progress-circular
              v-if="loadingNext"
              indeterminate
              size="16"
              color="white"
              class="mr-1"
            />
            <template v-else
              >Next<v-icon right>fas fa-chevron-right</v-icon></template
            >
          </v-btn>
        </div>
      </template>

      <!-- No events found -->
      <div v-else class="zvp-center-screen">
        <v-icon size="56" color="grey lighten-1">mdi-video-off-outline</v-icon>
        <p class="body-2 grey--text mt-3 text-center px-6">
          No recorded clips found for this incident.
        </p>
      </div>

      <!-- Private link footer -->
      <div class="zvp-private-footer">
        <v-icon x-small color="amber darken-2" class="mr-1">mdi-lock</v-icon>
        Authenticated · This link is private. Do not share it.
      </div>
    </template>

    <!-- ── NETWORK / UNEXPECTED ERROR ──────────────────────── -->
    <div v-else-if="state === 'error'" class="zvp-center-screen">
      <img
        src="/assets/SDLOGO_40.png"
        alt="StorageDefender"
        style="max-width: 160px"
        class="mb-5"
      />
      <v-icon size="48" color="grey" class="mb-3">mdi-wifi-off</v-icon>
      <div class="text-h6 mb-2">Unable to load video</div>
      <div class="body-2 grey--text text-center px-6">
        Could not connect to the server. Please check your connection and try
        again.
      </div>
    </div>

    <!-- ── EXPIRED / INVALID ─────────────────────────────────── -->
    <div v-else class="zvp-expired-screen">
      <div class="zvp-expired-banner">
        <v-icon color="amber darken-3" small class="mr-1">mdi-alert</v-icon>
        <span v-if="resendStatus === 'sent'">
          Session expired. A new link has been sent to your phone.
        </span>
        <span v-else-if="resendStatus === 'failed'">
          Session expired. We couldn&rsquo;t send a new link automatically.
        </span>
        <span v-else> Session Expired. Please contact facility. </span>
      </div>
      <div class="zvp-expired-body">
        <img
          src="/assets/SDLOGO_40.png"
          alt="StorageDefender"
          class="zvp-expired-logo"
        />
        <div class="zvp-expired-title">Welcome Back</div>
        <v-icon size="80" color="#c0392b" class="my-4">mdi-shield-lock</v-icon>
        <p v-if="resendStatus === 'sent'" class="body-2 text-center px-6">
          A new link has been sent to your phone.<br />
          Please check your <strong>text message app</strong>.<br />
          You may now close this page.
        </p>
        <p
          v-else-if="resendStatus === 'failed'"
          class="body-2 text-center px-6"
        >
          Your link has expired and we could not send a new one
          automatically.<br />
          Please contact the facility directly.
        </p>
        <p v-else class="body-2 text-center px-6">
          Your session has expired.<br />
          Please contact the facility directly.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
const GQL_ENDPOINT =
  import.meta.env.VUE_APP_GRAPHQL_HTTP || "http://localhost:4000/graphql";

const MOTION = "een.motionDetectionEvent.v1";
const PERSON = "een.personDetectionEvent.v1";

async function gql(query, variables) {
  const res = await fetch(GQL_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  return res.json();
}

const VALIDATE_QUERY = `
  query ValidateZoneVideoToken($token: String!) {
    validateZoneVideoToken(token: $token) {
      actorId startTimestamp endedAt organizationId
      zoneName facilityName facilityPhone
      unitName cameraName
    }
  }
`;

const EVENTS_QUERY = `
  query PublicZoneEvents($token: String!, $eventType: String!, $pageToken: String, $pageSize: Int) {
    publicZoneEvents(token: $token, eventType: $eventType, pageToken: $pageToken, pageSize: $pageSize) {
      results { id startTimestamp endTimestamp actorId type thumbnail }
      nextPageToken
    }
  }
`;

const VIDEO_QUERY = `
  query PublicZoneVideoFeed($token: String!, $eventTimestamp: String) {
    publicZoneVideoFeed(token: $token, eventTimestamp: $eventTimestamp) { video }
  }
`;

const RESEND_MUTATION = `
  mutation ResendZoneVideoLink($token: String!) {
    resendZoneVideoLink(token: $token) {
      status
    }
  }
`;

export default {
  name: "PageZoneVideoPublic",

  data() {
    return {
      state: "loading", // 'loading' | 'valid' | 'invalid' | 'expired'
      tokenData: null,
      resendStatus: null, // null | 'sent' | 'failed' | 'disabled'

      // events
      eventsLoading: false,
      noEventsDetected: false,
      thumbnailsList: [],
      nextPageToken: null,
      currentIndex: 0,
      loadingNext: false,

      // event type detection
      hasBothEventTypes: false,
      selectedEventType: null,
      currentEventType: null,
      skipWatcher: false,

      // video
      videoData: null,
      videoLoading: false,
      videoKey: 0,
    };
  },

  computed: {
    currentThumb() {
      return this.thumbnailsList[this.currentIndex] || null;
    },
    token() {
      return this.$route.query.token || null;
    },
  },

  watch: {
    selectedEventType(newType) {
      if (!newType || !this.hasBothEventTypes || this.skipWatcher) return;
      this.switchEventType(newType);
    },
  },

  async mounted() {
    if (!this.token) {
      this.state = "invalid";
      return;
    }

    try {
      const json = await gql(VALIDATE_QUERY, { token: this.token });
      if (json.data?.validateZoneVideoToken) {
        this.tokenData = json.data.validateZoneVideoToken;
        this.state = "valid";
        this.initEvents();
      } else {
        const err = json.errors?.[0];
        const code = err?.extensions?.code || err?.message;
        if (code === "LINK_EXPIRED" || code === "link_expired") {
          this.state = "expired";
          if (err?.extensions?.resend_enabled === false) {
            this.resendStatus = "disabled";
          } else {
            this.maybeAutoResend();
          }
        } else {
          this.state = "invalid";
        }
      }
    } catch (e) {
      console.error("PageZoneVideoPublic:", e);
      this.state = "error";
    }
  },

  methods: {
    // Re-opening the same expired SMS link must not fire a fresh SMS each time.
    // Only auto-resend once per token from this browser; the server-side cooldown
    // is the authoritative guard against abuse (the link is public).
    maybeAutoResend() {
      const key = `zvl_resent_${this.token}`;
      try {
        if (localStorage.getItem(key)) {
          this.resendStatus = "sent";
          return;
        }
      } catch {
        // localStorage unavailable (private mode) — fall through to a single send
      }
      this.triggerResend(key);
    },

    async triggerResend(dedupeKey = null) {
      try {
        const json = await gql(RESEND_MUTATION, { token: this.token });
        const status = json.data?.resendZoneVideoLink?.status;
        if (status === "sent") {
          this.resendStatus = "sent";
          if (dedupeKey) {
            try {
              localStorage.setItem(dedupeKey, String(Date.now()));
            } catch {
              // ignore — best-effort dedupe only
            }
          }
        } else if (status === "disabled") {
          this.resendStatus = "disabled";
        } else {
          this.resendStatus = "failed";
        }
      } catch {
        this.resendStatus = "failed";
      }
    },

    formatTs(iso) {
      if (!iso) return "N/A";
      try {
        return new Date(iso).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
      } catch {
        return iso;
      }
    },

    async fetchEvents(eventType, pageToken = null) {
      const json = await gql(EVENTS_QUERY, {
        token: this.token,
        eventType,
        pageToken,
        pageSize: 10,
      });
      return {
        results: json.data?.publicZoneEvents?.results || [],
        nextPageToken: json.data?.publicZoneEvents?.nextPageToken || null,
      };
    },

    async initEvents() {
      this.eventsLoading = true;
      try {
        // Probe both types in parallel — exactly 2 calls
        const [motionRes, personRes] = await Promise.all([
          this.fetchEvents(MOTION),
          this.fetchEvents(PERSON),
        ]);

        const hasMotion = motionRes.results.length > 0;
        const hasPerson = personRes.results.length > 0;
        this.hasBothEventTypes = hasMotion && hasPerson;

        if (hasMotion || hasPerson) {
          const preferred = hasMotion ? MOTION : PERSON;
          const preferredRes = hasMotion ? motionRes : personRes;

          this.currentEventType = preferred;
          this.skipWatcher = true;
          this.selectedEventType = preferred;
          this.skipWatcher = false;

          this.thumbnailsList = preferredRes.results;
          this.nextPageToken = preferredRes.nextPageToken;
          this.noEventsDetected = false;

          // Auto-load first clip immediately
          this.loadVideo();
        } else {
          this.noEventsDetected = true;
        }
      } catch (e) {
        console.error("PageZoneVideoPublic initEvents:", e);
        this.noEventsDetected = true;
      } finally {
        this.eventsLoading = false;
      }
    },

    async switchEventType(eventType) {
      this.currentEventType = eventType;
      this.thumbnailsList = [];
      this.nextPageToken = null;
      this.currentIndex = 0;
      this.videoData = null;
      this.noEventsDetected = false;
      this.eventsLoading = true;

      try {
        const res = await this.fetchEvents(eventType);
        this.thumbnailsList = res.results;
        this.nextPageToken = res.nextPageToken;
        if (res.results.length === 0) {
          this.noEventsDetected = true;
        } else {
          this.loadVideo();
        }
      } catch (e) {
        console.error("PageZoneVideoPublic switchEventType:", e);
      } finally {
        this.eventsLoading = false;
      }
    },

    prevClip() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.videoData = null;
        this.loadVideo();
      }
    },

    async nextClip() {
      if (this.currentIndex < this.thumbnailsList.length - 1) {
        this.currentIndex++;
        this.videoData = null;
        this.loadVideo();
      } else if (this.nextPageToken) {
        this.loadingNext = true;
        try {
          const res = await this.fetchEvents(
            this.currentEventType,
            this.nextPageToken
          );
          this.thumbnailsList = [...this.thumbnailsList, ...res.results];
          this.nextPageToken = res.nextPageToken;
          this.currentIndex++;
          this.videoData = null;
          this.loadVideo();
        } finally {
          this.loadingNext = false;
        }
      }
    },

    async loadVideo() {
      const thumb = this.thumbnailsList[this.currentIndex];
      if (!thumb || this.videoLoading) return;

      this.videoLoading = true;
      try {
        const json = await gql(VIDEO_QUERY, {
          token: this.token,
          eventTimestamp: thumb.startTimestamp,
        });
        const video = json.data?.publicZoneVideoFeed?.video;
        if (video) {
          this.videoData = video;
          this.videoKey++;
        }
      } catch (e) {
        console.error("PageZoneVideoPublic loadVideo:", e);
      } finally {
        this.videoLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Base ─────────────────────────────────────────────────── */
.zvp-page {
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

/* ── Centered states (loading / error) ────────────────────── */
.zvp-center-screen {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
}

/* ── Header ───────────────────────────────────────────────── */
.zvp-header {
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 10px 16px;
  display: flex;
  align-items: center;
}
.zvp-logo {
  height: 32px;
}

/* ── Alert banner ─────────────────────────────────────────── */
.zvp-alert-banner {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  margin: 10px 12px;
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.zvp-alert-title {
  font-weight: 700;
  font-size: 13px;
  color: #e65100;
}
.zvp-alert-sub {
  font-size: 12px;
  color: #555;
  margin-top: 2px;
}

/* ── Incident details card ────────────────────────────────── */
.zvp-details-card {
  background: #fff;
  margin: 0 12px 10px;
  border-radius: 6px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.zvp-details-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #9e9e9e;
  margin-bottom: 8px;
}
.zvp-details-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 5px 14px;
}
.zvp-dl {
  font-size: 12px;
  color: #9e9e9e;
  align-self: center;
  white-space: nowrap;
}
.zvp-dv {
  font-size: 12px;
  font-weight: 600;
  color: #212121;
}
.zvp-status {
  font-size: 12px;
}
.zvp-status--active {
  color: #db0808;
}
.zvp-status--resolved {
  color: #757575;
}

/* ── Clip header (dark bar with camera name + CLIP badge) ─── */
.zvp-clip-header {
  background: #1a1a2e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  font-size: 12px;
}
.zvp-clip-cam {
  font-weight: 600;
}
.zvp-clip-badge {
  background: #e63946;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 3px;
}

/* ── Event type radio ─────────────────────────────────────── */
.zvp-radio-row {
  background: #fff;
  padding: 4px 16px;
}

/* ── Player area ──────────────────────────────────────────── */
.zvp-player-area {
  background: #000;
  position: relative;
  width: 100%;
}
.zvp-video {
  width: 100%;
  max-height: 60vh;
  display: block;
  background: #000;
}
.zvp-thumb-wrap {
  position: relative;
  width: 100%;
  cursor: pointer;
}
.zvp-thumb-img {
  width: 100%;
  max-height: 60vh;
  object-fit: cover;
  display: block;
}
.zvp-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}
.zvp-thumb-ts {
  position: absolute;
  bottom: 8px;
  left: 10px;
  font-size: 11px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
}
.zvp-motion-badge {
  background: #e63946;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 3px;
}

.zvp-player-skeleton {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #111;
}
.zvp-player-skeleton .v-progress-circular {
  opacity: 0.7;
}

/* ── Clip footer (camera label + download) ────────────────── */
.zvp-clip-footer {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-top: 1px solid #eee;
}
.zvp-clip-footer-label {
  font-size: 10px;
  color: #9e9e9e;
}
.zvp-clip-footer-cam {
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
}
.zvp-download-link {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #2d2e75;
  text-decoration: none;
  font-weight: 600;
}
.zvp-download-link:hover {
  text-decoration: underline;
}

/* ── Navigation ───────────────────────────────────────────── */
.zvp-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 14px;
  background: #fff;
  margin-bottom: 8px;
}
.zvp-nav-btn {
  color: #fff !important;
  font-weight: 600;
  text-transform: none;
  min-width: 88px;
}
.zvp-prev {
  background-color: #e63946 !important;
}
.zvp-next {
  background-color: #2d2e75 !important;
}
.zvp-page-count {
  color: #555;
}

/* ── Private footer ───────────────────────────────────────── */
.zvp-private-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  font-size: 11px;
  color: #9e9e9e;
  margin-top: auto;
}

/* ── Expired screen ───────────────────────────────────────── */
.zvp-expired-screen {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.zvp-expired-banner {
  background: #fff8e1;
  border-bottom: 1px solid #f9a825;
  padding: 10px 16px;
  font-size: 13px;
  color: #5d4037;
  display: flex;
  align-items: center;
}
.zvp-expired-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
}
.zvp-expired-logo {
  height: 32px;
  margin-bottom: 16px;
}
.zvp-expired-title {
  font-size: 26px;
  font-weight: 700;
  color: #c0392b;
  margin-bottom: 4px;
}
</style>
